import { ReloadOutlined } from '@ant-design/icons';
import type { InboundRow, OutboundRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatDisplayTime, formatQty } from '@hq/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Form,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { sumWidth } from '@hq/ui-kit';
import { hq } from '../session';

const OUT_STATUS: Record<string, string> = { NEW: '待拣货', PICKING: '拣货中', PICKED: '已拣货', DONE: '已完成', CANCELED: '已取消' };
const IN_STATUS: Record<string, string> = { NEW: '待收货', CHECKED: '已验收', DONE: '已入库', CANCELED: '已取消' };

/** 仓内作业：入库上架 + 出库拣货（WL 库存管理的一部分，不做货位级扣减）。 */
export default function WmsOps() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('inbound');
  const [currentIn, setCurrentIn] = useState<InboundRow | null>(null);
  const [currentOut, setCurrentOut] = useState<OutboundRow | null>(null);
  const [putawayOpen, setPutawayOpen] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  const [putawayForm] = Form.useForm();
  const [pickForm] = Form.useForm();

  const inbound = useQuery({
    queryKey: ['ops', 'wms', 'inbound'],
    queryFn: () => hq.inbound.list({ size: 100 })
  });
  const outbound = useQuery({
    queryKey: ['ops', 'wms', 'outbound'],
    queryFn: () => hq.outbound.list({ size: 100 })
  });
  const inboundDetail = useQuery({
    queryKey: ['ops', 'wms', 'inbound', 'detail', currentIn?.id],
    enabled: Boolean(currentIn),
    queryFn: () => hq.inbound.detail(currentIn?.id ?? '')
  });
  const outboundDetail = useQuery({
    queryKey: ['ops', 'wms', 'outbound', 'detail', currentOut?.id],
    enabled: Boolean(currentOut),
    queryFn: () => hq.outbound.detail(currentOut?.id ?? '')
  });
  const locations = useQuery({
    queryKey: ['ops', 'wms', 'locations', currentIn?.warehouseId ?? currentOut?.warehouseId],
    queryFn: () =>
      hq.orgStructure.location.list({
        warehouseId: (currentIn?.warehouseId ?? currentOut?.warehouseId ?? undefined) as string | undefined
      })
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['ops', 'wms'] });
  };

  const putaway = useMutation({
    mutationFn: async () => {
      const values = await putawayForm.validateFields();
      const items = (inboundDetail.data?.items ?? [])
        .filter((item) => values[`loc_${item.id}`])
        .map((item) => ({ itemId: item.id, locationId: String(values[`loc_${item.id}`]) }));
      if (items.length === 0) throw new ApiError({ message: '请至少选择一行货位', code: 400 });
      return hq.inbound.putaway(currentIn?.id ?? '', { items });
    },
    onSuccess: async (result) => {
      message.success(`上架登记成功（${result.putawayLines} 行）`);
      setPutawayOpen(false);
      await refresh();
      await inboundDetail.refetch();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '上架失败')
  });

  const pick = useMutation({
    mutationFn: async () => {
      const values = await pickForm.validateFields();
      const items = (outboundDetail.data?.items ?? []).map((item) => ({
        itemId: item.id,
        pickQty: Number(values[`qty_${item.id}`] ?? 0),
        locationId: String(values[`loc_${item.id}`] ?? '0')
      }));
      return hq.outbound.pick(currentOut?.id ?? '', { items });
    },
    onSuccess: async (result) => {
      message.success(`拣货登记成功，单据状态：${OUT_STATUS[result.status] ?? result.status}`);
      setPickOpen(false);
      await refresh();
      await outboundDetail.refetch();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '拣货失败')
  });

  const inColumns: ColumnsType<InboundRow> = [
    { title: '入库单号', dataIndex: 'bizNo', width: 170 },
    { title: '来源', dataIndex: 'inboundType', width: 120, render: (value: string) => <Tag>{value}</Tag> },
    { title: '状态', dataIndex: 'inboundStatus', width: 100, render: (value: string) => <Tag color={value === 'DONE' ? 'green' : 'blue'}>{IN_STATUS[value] ?? value}</Tag> },
    { title: '数量', dataIndex: 'totalQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '金额', dataIndex: 'totalAmount', width: 130, align: 'right' },
    { title: '创建时间', dataIndex: 'createAt', width: 160, render: (value: string) => formatDisplayTime(value) },
    {
      title: '操作',
      width: 120,
      render: (_, row) => (
        <Button
          type="link"
          disabled={row.inboundStatus === 'DONE' || row.inboundStatus === 'CANCELED'}
          onClick={async () => {
            setCurrentIn(row);
            putawayForm.resetFields();
            setPutawayOpen(true);
          }}
        >
          上架登记
        </Button>
      )
    }
  ];

  const outColumns: ColumnsType<OutboundRow> = [
    { title: '出库单号', dataIndex: 'bizNo', width: 170 },
    { title: '来源', dataIndex: 'outboundType', width: 130, render: (value: string) => <Tag>{value}</Tag> },
    { title: '状态', dataIndex: 'outboundStatus', width: 100, render: (value: string) => <Tag color={value === 'DONE' ? 'green' : 'blue'}>{OUT_STATUS[value] ?? value}</Tag> },
    { title: '数量', dataIndex: 'totalQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
    { title: '金额', dataIndex: 'totalAmount', width: 130, align: 'right' },
    { title: '创建时间', dataIndex: 'createAt', width: 160, render: (value: string) => formatDisplayTime(value) },
    {
      title: '操作',
      width: 120,
      render: (_, row) => (
        <Button
          type="link"
          disabled={row.outboundStatus === 'DONE' || row.outboundStatus === 'CANCELED'}
          onClick={() => {
            setCurrentOut(row);
            pickForm.resetFields();
            setPickOpen(true);
          }}
        >
          拣货登记
        </Button>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title={
        <Space>
          仓内作业
          <Typography.Text type="secondary">入库上架 / 出库拣货（PDA 与 PC 同源，扣减仍在确认出入库时执行）</Typography.Text>
        </Space>
      }
      extra={
        <Button icon={<ReloadOutlined />} onClick={() => refresh()} loading={inbound.isFetching || outbound.isFetching}>
          刷新
        </Button>
      }
    >
      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: 'inbound',
            label: `入库上架（${inbound.data?.items.length ?? 0}）`,
            children: (
              <>
                {inbound.isError ? <Alert type="error" showIcon style={{ marginBottom: 12 }} message="入库单加载失败" /> : null}
                <Table<InboundRow>
                  rowKey="id"
                  size="middle"
                  columns={inColumns}
                  dataSource={inbound.data?.items ?? []}
                  loading={inbound.isPending}
                  pagination={{ pageSize: 15, hideOnSinglePage: true }}
                  scroll={{ x: sumWidth(inColumns) }}
                />
              </>
            )
          },
          {
            key: 'outbound',
            label: `出库拣货（${outbound.data?.items.length ?? 0}）`,
            children: (
              <>
                {outbound.isError ? <Alert type="error" showIcon style={{ marginBottom: 12 }} message="出库单加载失败" /> : null}
                <Table<OutboundRow>
                  rowKey="id"
                  size="middle"
                  columns={outColumns}
                  dataSource={outbound.data?.items ?? []}
                  loading={outbound.isPending}
                  pagination={{ pageSize: 15, hideOnSinglePage: true }}
                  scroll={{ x: sumWidth(outColumns) }}
                />
              </>
            )
          }
        ]}
      />

      <Modal
        title={`上架登记 · ${currentIn?.bizNo ?? ''}`}
        open={putawayOpen}
        onCancel={() => setPutawayOpen(false)}
        onOk={() => putawayForm.submit()}
        confirmLoading={putaway.isPending}
        width={820}
        destroyOnHidden
      >
        {inboundDetail.isError ? (
          <Alert type="error" showIcon style={{ marginBottom: 12 }} message="明细加载失败" />
        ) : (
          <Form form={putawayForm} layout="vertical" onFinish={() => putaway.mutate()} preserve={false}>
            <Descriptions size="small" column={3} style={{ marginBottom: 12 }}>
              <Descriptions.Item label="单据">{currentIn?.bizNo}</Descriptions.Item>
              <Descriptions.Item label="状态">{IN_STATUS[currentIn?.inboundStatus ?? ''] ?? currentIn?.inboundStatus}</Descriptions.Item>
              <Descriptions.Item label="货位数">{locations.data?.length ?? 0}</Descriptions.Item>
            </Descriptions>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              loading={Boolean(currentIn) && inboundDetail.isPending}
              dataSource={inboundDetail.data?.items ?? []}
              columns={[
                { title: '物资', width: 240, render: (_v, row) => `${row.materialCode ?? ''} ${row.materialName ?? ''}` },
                { title: '批次', dataIndex: 'batchNo', width: 120, render: (v: string | null) => v || '-' },
                { title: '合格数量', dataIndex: 'qualifiedQty', width: 100, align: 'right', render: (v: number) => formatQty(v) },
                {
                  title: '上架货位',
                  width: 220,
                  render: (_v, row) => (
                    <Form.Item name={`loc_${row.id}`} style={{ marginBottom: 0 }}>
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder={row.locationId && row.locationId !== '0' ? '已上架（可改）' : '选择货位'}
                        options={(locations.data ?? []).map((loc) => ({ value: loc.id, label: `${loc.locationCode}（${loc.zoneType ?? '-'}）` }))}
                      />
                    </Form.Item>
                  )
                }
              ]}
            />
          </Form>
        )}
      </Modal>

      <Modal
        title={`拣货登记 · ${currentOut?.bizNo ?? ''}`}
        open={pickOpen}
        onCancel={() => setPickOpen(false)}
        onOk={() => pickForm.submit()}
        confirmLoading={pick.isPending}
        width={860}
        destroyOnHidden
      >
        {outboundDetail.isError ? (
          <Alert type="error" showIcon style={{ marginBottom: 12 }} message="明细加载失败" />
        ) : (
          <Form form={pickForm} layout="vertical" onFinish={() => pick.mutate()} preserve={false}>
            <Descriptions size="small" column={3} style={{ marginBottom: 12 }}>
              <Descriptions.Item label="单据">{currentOut?.bizNo}</Descriptions.Item>
              <Descriptions.Item label="状态">{OUT_STATUS[currentOut?.outboundStatus ?? ''] ?? currentOut?.outboundStatus}</Descriptions.Item>
              <Descriptions.Item label="说明">确认出库时按拣货量扣减</Descriptions.Item>
            </Descriptions>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              loading={Boolean(currentOut) && outboundDetail.isPending}
              dataSource={outboundDetail.data?.items ?? []}
              columns={[
                { title: '物资', width: 240, render: (_v, row) => `${row.materialCode ?? ''} ${row.materialName ?? ''}` },
                { title: '批次', dataIndex: 'batchNo', width: 120, render: (v: string | null) => v || '-' },
                { title: '计划量', dataIndex: 'planQty', width: 90, align: 'right', render: (v: number) => formatQty(v) },
                {
                  title: '拣货量',
                  width: 120,
                  render: (_v, row) => (
                    <Form.Item name={`qty_${row.id}`} style={{ marginBottom: 0 }}>
                      <InputNumber min={0} max={row.planQty} defaultValue={row.pickQty > 0 ? row.pickQty : row.planQty} style={{ width: '100%' }} />
                    </Form.Item>
                  )
                },
                {
                  title: '拣货货位',
                  width: 200,
                  render: (_v, row) => (
                    <Form.Item name={`loc_${row.id}`} style={{ marginBottom: 0 }}>
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="选择货位"
                        options={(locations.data ?? []).map((loc) => ({ value: loc.id, label: loc.locationCode }))}
                      />
                    </Form.Item>
                  )
                }
              ]}
            />
          </Form>
        )}
      </Modal>

    </Card>
  );
}
